/**
 * Claude 對話 WebSocket hook
 *
 * - Auth：依賴 cookie（瀏覽器同 origin 自動帶）+ query string `?csrf=<token>`（後端比對 cookie）
 * - 自動指數退避重連（500 → 1000 → 2000 → 5000ms 上限）
 * - 接收每個 server frame 都觸發 onEvent
 * - 卸載時自動關閉
 *
 * 用法：
 *   const { send, status, close } = useClaudeWS({
 *     conversationId,
 *     enabled: true,
 *     onEvent: (frame) => {...},
 *   });
 *   send({ type:'instruction', instruction:'...', mode:'query', file_ids: [...] });
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { getCsrfToken } from '../utils/auth';

const BACKOFF_MS = [500, 1000, 2000, 5000];

/**
 * 把 backendUrl（可能是 "/api" 或 "http://..."）轉成完整 ws:// / wss:// URL
 */
function buildWsUrl(backendUrl, conversationId, csrf) {
  const path = `/users/admin/claude/conversations/${encodeURIComponent(conversationId)}/ws`;
  const qs = csrf ? `?csrf=${encodeURIComponent(csrf)}` : '';

  // 絕對 URL（http:// 或 https://）：替換 scheme 即可
  if (/^https?:\/\//i.test(backendUrl)) {
    const u = new URL(backendUrl);
    u.protocol = u.protocol === 'https:' ? 'wss:' : 'ws:';
    // 保留 backendUrl 的 path prefix，再接上 path
    const basePath = u.pathname.replace(/\/$/, '');
    return `${u.protocol}//${u.host}${basePath}${path}${qs}`;
  }

  // 相對 URL（如 "/api"）：用 window.location 推 host
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const basePath = (backendUrl || '').replace(/\/$/, '');
  return `${proto}//${window.location.host}${basePath}${path}${qs}`;
}

export function useClaudeWS({ backendUrl, conversationId, enabled, onEvent }) {
  const wsRef = useRef(null);
  const retryRef = useRef(0);
  const reconnectTimerRef = useRef(null);
  const onEventRef = useRef(onEvent);
  const [status, setStatus] = useState('idle'); // idle | connecting | open | closed | error

  // 保持 onEvent 最新但不觸發 reconnect
  useEffect(() => {
    onEventRef.current = onEvent;
  }, [onEvent]);

  const cleanup = useCallback(() => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    if (wsRef.current) {
      try {
        wsRef.current.onopen = null;
        wsRef.current.onmessage = null;
        wsRef.current.onclose = null;
        wsRef.current.onerror = null;
        if (
          wsRef.current.readyState === WebSocket.OPEN ||
          wsRef.current.readyState === WebSocket.CONNECTING
        ) {
          wsRef.current.close(1000);
        }
      } catch (_) {
        /* ignore */
      }
      wsRef.current = null;
    }
  }, []);

  const connect = useCallback(() => {
    if (!conversationId || !enabled) return;
    setStatus('connecting');
    const csrf = getCsrfToken();
    const url = buildWsUrl(backendUrl, conversationId, csrf);

    let ws;
    try {
      ws = new WebSocket(url);
    } catch (e) {
      setStatus('error');
      onEventRef.current?.({ type: 'error', data: `WebSocket 建立失敗: ${e.message}` });
      return;
    }
    wsRef.current = ws;

    ws.onopen = () => {
      retryRef.current = 0;
      setStatus('open');
    };

    ws.onmessage = (ev) => {
      let payload;
      try {
        payload = JSON.parse(ev.data);
      } catch (_) {
        return;
      }
      onEventRef.current?.(payload);
    };

    ws.onerror = () => {
      setStatus('error');
    };

    ws.onclose = (ev) => {
      setStatus('closed');
      // 1000=正常關閉、4404=對話不存在、4401/1008=auth fail：不重連
      const noRetryCodes = [1000, 1008, 4401, 4404];
      if (!enabled || noRetryCodes.includes(ev.code)) {
        return;
      }
      // 指數退避重連
      const delay = BACKOFF_MS[Math.min(retryRef.current, BACKOFF_MS.length - 1)];
      retryRef.current += 1;
      onEventRef.current?.({ type: 'reconnecting', delay, attempt: retryRef.current });
      reconnectTimerRef.current = setTimeout(connect, delay);
    };
  }, [backendUrl, conversationId, enabled]);

  useEffect(() => {
    if (!enabled || !conversationId) {
      cleanup();
      setStatus('idle');
      return;
    }
    connect();
    return cleanup;
  }, [enabled, conversationId, connect, cleanup]);

  const send = useCallback((payload) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      onEventRef.current?.({ type: 'error', data: 'WebSocket 尚未連線，無法傳送' });
      return false;
    }
    ws.send(JSON.stringify(payload));
    return true;
  }, []);

  return { status, send, close: cleanup };
}
